/**
 * The output field mapping of one target table, as pairs: on the left what
 * the workflow reports, on the right the column it fills. The workflow
 * decides the rows, not the table: each response field is one row whose
 * column is chosen; a table with many columns adds none. Other values (an
 * element of a split list, a row written to an earlier table, a fixed
 * option) are added as rows of their own. "Auto-Zuordnung" proposes the
 * columns for all response fields.
 *
 * Response fields come from the workflow's End step. Without any, the grid
 * says where they are defined and shows the mappings that are already saved.
 * How rows are written (new, update, upsert) is set under "Zeilen
 * schreiben"; the grid keeps that setting's keys in step with the mappings.
 */

import { Button, notify, Switch } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { AiIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EditorService } from '../../../editor';
import { WorkbenchService } from '../../../workbench/services/workbench';
import type {
  MatchConfigProps,
  MultiMatchBehavior,
  OutputMapping,
  OutputTable,
  RelationMatchMode,
  StealBehavior,
} from '../../services/automation';
import {
  type ColumnInfo,
  DatabaseDiscoveryService,
} from '../../services/database-discovery';
import { FullscreenRegistry } from '../../services/fullscreen-registry';
import { WorkflowService } from '../../services/workflow';
import { AutoMatchDialog } from './auto-match-dialog';
import * as styles from './field-mapping-grid.css';
import { isFileIndexSystemColumn, isFileIndexTable } from './file-index-table';
import { MappingCell } from './mapping-cell';
import {
  MATCH_MODE_WITH_AUTO_OPTIONS,
  MatchConfigEditor,
} from './match-config-editor';
import { buildOutputMappingRows } from './output-mapping-rows';
import {
  applyMappingChange,
  assignOutputColumn,
  droppedMatchConfigIds,
  type OutputMappingSource,
} from './output-mapping-writer';
import { persistReferencedAlias, targetAliasOf } from './output-target-alias';
import { createMatchConfigId, syncRowKeys } from './row-write';
import {
  SectionAddLink,
  SectionControlRow,
  SectionHint,
  SectionMappingGrid,
  SectionMappingPair,
  SectionSkeleton,
  SectionStack,
} from './section-layout';
import { CompatDot } from './type-chip';
import type { FieldOptionGroup } from './typed-field-select';
import { TypedFieldSelect } from './typed-field-select';

export const AUTOMATION_MAPPING_LIMIT = 32;

interface FieldMappingGridProps {
  /** Target table of the mappings. */
  tableId?: string;
  /** Target table columns */
  targetColumns: ColumnInfo[];
  /** Current mappings */
  mappings: OutputMapping[];
  /** Callback when mappings change */
  onMappingsChange: (mappings: OutputMapping[]) => void;
  /** Match configurations (row matching and relation lookups). */
  matchConfigs?: MatchConfigProps[];
  onMatchConfigsChange?: (configs: MatchConfigProps[]) => void;
  /** All output tables, in the order they are written. */
  outputTables?: OutputTable[];
  onOutputTablesChange?: (tables: OutputTable[]) => void;
  /** Source table columns for resolving column IDs to names */
  sourceColumns?: ColumnInfo[];
  /** Whether a workflow is selected: without one there are no response fields. */
  hasWorkflow: boolean;
  /** The selected workflow's response fields are still loading. */
  schemaLoading?: boolean;
  /** Opens the workflow, where its response fields are defined. */
  onOpenWorkflow?: (() => void) | null;
}

/**
 * Build a map from column ID to column name
 */
function buildColumnIdToNameMap(columns: ColumnInfo[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const col of columns) {
    map.set(col.id, col.name);
  }
  return map;
}

/**
 * Resolve a field path like "payload.h1scAM_byy.value" to "ColumnName"
 * Handles payload prefix, resolves column IDs to names, removes .value suffix
 */
function resolveFieldPath(
  path: string,
  columnMap: Map<string, string>
): string {
  // Split path into segments
  const segments = path.split('.');

  // Find which segment might be a column ID
  // Could be first segment or second (after "payload")
  for (let i = 0; i < segments.length; i++) {
    const columnName = columnMap.get(segments[i]);
    if (columnName) {
      segments[i] = columnName;
      // Only resolve first match
      break;
    }
  }

  // Remove payload prefix for cleaner display
  if (segments[0] === 'payload') {
    segments.shift();
  }

  // Remove .value suffix for cleaner display (keep the actual path for data access)
  if (segments[segments.length - 1] === 'value') {
    segments.pop();
  }

  return segments.join('.');
}

function toFanoutItemFieldPath(value: string): string | null {
  const words = value
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return null;
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) return lower;
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join('');
}

function normalizeOutputFieldType(type: string): string {
  if (type === 'rich-text' || type === 'title') return 'text';
  if (type === 'relation') return 'text';
  return type;
}

// sl-1ahs.6.4: isYourDataField / shouldHideField / isSystemField helpers and
// the SYSTEM_FIELDS allow-list were removed together with the response-based
// field extraction path; the grid now derives fields exclusively from the
// workflow End node output schema.

/**
 * Type compatibility matrix
 * Returns: 'ok' (direct), 'cast' (needs conversion), 'error' (incompatible)
 */
function getTypeCompatibility(
  sourceType: string,
  targetType: string
): 'ok' | 'cast' | 'error' {
  // Normalize types
  const source = sourceType.toLowerCase().replace(/\[\d+\]/, '');
  const target = targetType.toLowerCase();

  // Direct compatibility
  if (source === target) return 'ok';

  // String can be cast to most types
  if (source === 'string') {
    if (['rich-text', 'text', 'title'].includes(target)) return 'ok';
    // sl-hih8: Relation columns are compatible — resolved via SmartMatchingService
    if (target === 'relation') return 'ok';
    if (['number', 'date', 'select', 'multi-select'].includes(target))
      return 'cast';
    return 'cast'; // Try anyway
  }

  // Number compatibility
  if (source === 'number') {
    if (['number', 'progress'].includes(target)) return 'ok';
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  // Boolean compatibility
  if (source === 'boolean') {
    if (target === 'checkbox') return 'ok';
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  // Array - usually incompatible except for multi-select
  if (source.startsWith('array')) {
    if (target === 'multi-select') return 'cast';
    return 'error';
  }

  // Object - usually incompatible
  if (source === 'object') {
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  return 'cast'; // Default: try casting
}

/**
 * S020-8.20.2: Check if column is a relation type
 */
function isRelationColumn(columnType: string): boolean {
  return columnType.toLowerCase() === 'relation';
}

/**
 * sl-vb5r: Resolve a select option ID to its display label
 */
function resolveOptionLabel(column: ColumnInfo, optionId: string): string {
  const options =
    (column.data?.options as Array<{ id: string; value: string }>) ?? [];
  return options.find(o => o.id === optionId)?.value ?? optionId;
}

const COMPATIBILITY = {
  ok: 'compatible',
  cast: 'castable',
  error: 'incompatible',
} as const;

const FIXED_PREFIX = 'fixed:';

/** How a value of the left type fits a column of the right type. */
const columnCompatibility = (sourceType: string, columnType: string) =>
  COMPATIBILITY[getTypeCompatibility(sourceType, columnType)];

/** A row that is being added: what it writes and where, as far as chosen. */
interface DraftRow {
  source: string;
  columnId: string;
}
const EMPTY_DRAFT: DraftRow = { source: '', columnId: '' };

export const FieldMappingGrid: FC<FieldMappingGridProps> = ({
  tableId,
  targetColumns,
  mappings,
  onMappingsChange,
  matchConfigs = [],
  onMatchConfigsChange,
  outputTables = [],
  onOutputTablesChange,
  sourceColumns = [],
  hasWorkflow,
  schemaLoading = false,
  onOpenWorkflow,
}) => {
  const t = useI18n();
  const [isAutoMatchOpen, setIsAutoMatchOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRow | null>(null);

  // Services for relation column features
  const discoveryService = useService(DatabaseDiscoveryService);
  const editorService = useServiceOptional(EditorService);
  const fullscreenRegistry = useServiceOptional(FullscreenRegistry);
  const workbenchService = useService(WorkbenchService);
  const allDatabases = useLiveData(discoveryService.blocks$);

  // sl-tq87: Schema fallback for output field mapping
  const workflowService = useService(WorkflowService);
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);

  // Resolve relation target database info (name + columns) by targetDatabaseId
  const getRelationTargetInfo = useCallback(
    (
      column: ColumnInfo
    ): {
      tableName: string;
      tableId: string;
      docId: string;
      columns: ColumnInfo[];
    } | null => {
      if (!column.targetDatabaseId) return null;
      const targetDb = allDatabases?.find(
        (db: { blockId: string }) => db.blockId === column.targetDatabaseId
      );
      if (!targetDb) return null;
      return {
        tableName: targetDb.databaseTitle,
        tableId: targetDb.blockId,
        docId: targetDb.docId,
        columns: targetDb.columns,
      };
    },
    [allDatabases]
  );

  // Navigate to relation target database in fullscreen
  const handleOpenRelationTarget = useCallback(
    (column: ColumnInfo) => {
      const targetDocId = column.targetDocId;
      const targetDbId = column.targetDatabaseId;
      if (!targetDbId) return;

      const currentDocId = editorService?.editor.doc.id;

      if (!targetDocId || targetDocId === currentDocId) {
        // Same doc: open fullscreen directly via FullscreenRegistry
        fullscreenRegistry?.open('database', targetDbId);
      } else {
        // Cross-doc: navigate to doc with fullscreen query param
        workbenchService.workbench.open(
          `/${targetDocId}?fullscreenDatabaseId=${targetDbId}`
        );
      }
    },
    [editorService, fullscreenRegistry, workbenchService]
  );

  // sl-mau5: Helper to find MatchConfig for a relation mapping
  const getRelationMatchConfig = useCallback(
    (targetColumnId: string): MatchConfigProps | undefined => {
      const mapping = mappings.find(m => m.targetColumnId === targetColumnId);
      if (!mapping?.matchConfigId) return undefined;
      return matchConfigs.find(mc => mc.id === mapping.matchConfigId);
    },
    [mappings, matchConfigs]
  );

  // Handle lookupMatchColumn change for relation lookup
  const handleLookupMatchColumnChange = useCallback(
    (columnId: string, matchColumnId: string | undefined) => {
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId || !onMatchConfigsChange) return;
      const updatedConfigs = matchConfigs.map(mc => {
        if (mc.id === mapping.matchConfigId && mc.keys.length > 0) {
          const updatedKeys = [...mc.keys];
          updatedKeys[0] = {
            ...updatedKeys[0],
            columnId: matchColumnId ?? 'title',
          };
          return { ...mc, keys: updatedKeys };
        }
        return mc;
      });
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  // Build column ID to name map for resolving IDs
  const columnMap = useMemo(
    () => buildColumnIdToNameMap(sourceColumns),
    [sourceColumns]
  );

  // sl-czam: Map output field IDs to human-readable names from schema
  const schemaNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (workflowSchema?.output) {
      for (const [fieldId, def] of Object.entries(workflowSchema.output)) {
        const name = (def as { name?: string }).name;
        if (name) map.set(fieldId, name);
      }
    }
    return map;
  }, [workflowSchema]);

  // Helper to get display name for a field path
  const getDisplayName = useCallback(
    (path: string): string => {
      // Check schema name map first (field ID → human-readable name)
      const schemaName = schemaNameMap.get(path);
      if (schemaName) return schemaName;
      return resolveFieldPath(path, columnMap);
    },
    [columnMap, schemaNameMap]
  );

  // Available fields are derived exclusively from the workflow End node
  // output schema. sl-1ahs.6.4: the former response-based field extraction
  // branch was removed together with the cached lastResponse state; output
  // mapping correctness is now guaranteed at configure-time against this
  // schema (sl-1ahs.7.3).
  interface SchemaField {
    path: string;
    type: string;
    displayName: string;
    /** The workflow marks the field as one that must be written. */
    required?: boolean;
  }
  const currentOutputTable = useMemo(
    () => outputTables.find(table => table.tableId === tableId),
    [outputTables, tableId]
  );
  const currentOutputTableIndex = useMemo(
    () => outputTables.findIndex(table => table.tableId === tableId),
    [outputTables, tableId]
  );
  const groupedFields = useMemo((): {
    workflowResponse: SchemaField[];
    fanoutItem: SchemaField[];
    targetContext: SchemaField[];
    source: 'schema' | 'none';
  } => {
    const fanoutItemFields: SchemaField[] = [];
    if (currentOutputTable?.sourceArrayField) {
      const seen = new Set<string>();
      const addFanoutField = (
        path: string | null,
        displayName: string,
        type: string
      ) => {
        if (!path || seen.has(path)) return;
        seen.add(path);
        fanoutItemFields.push({ path, displayName, type });
      };

      for (const column of targetColumns) {
        if (column.type === 'relation') continue;
        addFanoutField(
          toFanoutItemFieldPath(column.name),
          column.name,
          normalizeOutputFieldType(column.type)
        );
      }
      addFanoutField(
        'item',
        t['com.streamlain.streamlain.output.fanout-item'](),
        'json'
      );
      addFanoutField(
        'value',
        t['com.streamlain.streamlain.output.fanout-item-value'](),
        'json'
      );
      addFanoutField(
        '__index',
        t['com.streamlain.streamlain.output.fanout-item-index'](),
        'number'
      );
      addFanoutField(
        '__item',
        t['com.streamlain.streamlain.output.fanout-item-raw'](),
        'json'
      );
    }

    // Rows written to the tables before this one can be linked from here.
    const targetContextFields = (
      currentOutputTableIndex > 0
        ? outputTables.slice(0, currentOutputTableIndex)
        : []
    )
      .filter(table => table.tableId)
      .flatMap(table => {
        const alias = targetAliasOf(table, outputTables);
        const name =
          table.tableName || t['com.streamlain.streamlain.common.untitled']();
        return [
          {
            path: `__targets.${alias}.rowId`,
            type: 'text',
            displayName: t['com.streamlain.streamlain.output.target-row']({
              table: name,
            }),
          },
          {
            path: `__targets.${alias}.rowIds`,
            type: 'json',
            displayName: t['com.streamlain.streamlain.output.target-rows']({
              table: name,
            }),
          },
        ];
      });

    if (workflowSchema?.output) {
      const schemaFields: SchemaField[] = Object.entries(
        workflowSchema.output
      ).map(([fieldId, fieldDef]) => ({
        path: fieldId,
        type: (fieldDef as { type?: string }).type || 'text',
        displayName: (fieldDef as { name?: string }).name || fieldId,
        required: !!(fieldDef as { required?: boolean }).required,
      }));

      if (schemaFields.length > 0) {
        return {
          workflowResponse: schemaFields,
          fanoutItem: fanoutItemFields,
          targetContext: targetContextFields,
          source: 'schema',
        };
      }
    }

    // No schema available — empty catalog
    return {
      workflowResponse: [],
      fanoutItem: fanoutItemFields,
      targetContext: targetContextFields,
      source: 'none',
    };
  }, [
    currentOutputTable?.sourceArrayField,
    currentOutputTableIndex,
    outputTables,
    t,
    targetColumns,
    workflowSchema,
  ]);

  // Flat list for compatibility checks
  const responseFields = useMemo(
    () => [
      ...groupedFields.workflowResponse,
      ...groupedFields.fanoutItem,
      ...groupedFields.targetContext,
    ],
    [groupedFields]
  );

  const filesTable = useMemo(
    () => isFileIndexTable(tableId, targetColumns),
    [tableId, targetColumns]
  );
  const isProtectedColumn = useCallback(
    (column: ColumnInfo) => filesTable && isFileIndexSystemColumn(column),
    [filesTable]
  );

  // Build FieldOptionGroup[] for TypedFieldSelect
  const responseFieldGroups = useMemo((): FieldOptionGroup[] => {
    const groups: FieldOptionGroup[] = [];
    if (groupedFields.workflowResponse.length > 0) {
      groups.push({
        id: 'workflow-response',
        label: t['com.streamlain.streamlain.output.group-response'](),
        options: groupedFields.workflowResponse.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    if (groupedFields.fanoutItem.length > 0) {
      groups.push({
        id: 'fanout-item',
        label: t['com.streamlain.streamlain.output.group-fanout-item'](),
        options: groupedFields.fanoutItem.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    if (groupedFields.targetContext.length > 0) {
      groups.push({
        id: 'target-context',
        label: t['com.streamlain.streamlain.output.group-created-rows'](),
        options: groupedFields.targetContext.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    return groups;
  }, [groupedFields, getDisplayName, t]);

  // Get response field info by path
  const getResponseFieldInfo = useCallback(
    (path: string) => {
      return responseFields.find(f => f.path === path);
    },
    [responseFields]
  );

  /**
   * Stores the mappings and what follows from them: the keys of "Zeilen
   * schreiben" compare the response field mapped to their column, a table
   * whose written rows are referenced keeps its reference name, and a
   * relation lookup goes with the mapping it belonged to.
   */
  const commitMappings = useCallback(
    (next: OutputMapping[]) => {
      onMappingsChange(next);
      if (!tableId) return;
      const dropped = new Set(droppedMatchConfigIds(mappings, next));
      const synced = syncRowKeys(
        {
          matchConfigs:
            dropped.size > 0
              ? matchConfigs.filter(config => !dropped.has(config.id))
              : matchConfigs,
          outputTables,
        },
        tableId,
        next
      );
      const tables = next
        .filter(mapping => mapping.tableId === tableId)
        .reduce(
          (current, mapping) =>
            persistReferencedAlias(current, mapping.responseField),
          synced.outputTables
        );
      if (synced.matchConfigs !== matchConfigs)
        onMatchConfigsChange?.(synced.matchConfigs);
      if (tables !== outputTables) onOutputTablesChange?.(tables);
    },
    [
      mappings,
      matchConfigs,
      onMappingsChange,
      onMatchConfigsChange,
      onOutputTablesChange,
      outputTables,
      tableId,
    ]
  );

  const warnSystemColumn = useCallback(
    () =>
      notify.warning({
        title: t['com.streamlain.automation.sidebar.files.system-column'](),
        message:
          t['com.streamlain.automation.sidebar.files.system-column-hint'](),
      }),
    [t]
  );
  const warnMappingLimit = useCallback(
    () =>
      notify.warning({
        title: t['com.streamlain.streamlain.mapping.limit-reached.title'](),
        message: t['com.streamlain.streamlain.mapping.limit-reached.output']({
          count: String(AUTOMATION_MAPPING_LIMIT),
        }),
      }),
    [t]
  );

  // The mapping math lives in the pure `applyMappingChange` helper, so the
  // spec drives the same code path the user does.
  const handleMappingChange = useCallback(
    (columnId: string, responseField: string): boolean => {
      const column = targetColumns.find(column => column.id === columnId);
      if (column && responseField && isProtectedColumn(column)) {
        warnSystemColumn();
        return false;
      }
      if (
        responseField &&
        !mappings.some(m => m.targetColumnId === columnId) &&
        mappings.length >= AUTOMATION_MAPPING_LIMIT
      ) {
        warnMappingLimit();
        return false;
      }
      commitMappings(
        applyMappingChange({
          prev: mappings,
          columnId,
          responseField,
          tableId,
          responseFields,
          getDisplayName,
        })
      );
      return true;
    },
    [
      commitMappings,
      getDisplayName,
      isProtectedColumn,
      mappings,
      responseFields,
      tableId,
      targetColumns,
      warnMappingLimit,
      warnSystemColumn,
    ]
  );

  // A select column can take one of its options as a fixed value.
  const handleFixedValueSelect = useCallback(
    (columnId: string, optionId: string): boolean => {
      const column = targetColumns.find(column => column.id === columnId);
      if (column && isProtectedColumn(column)) {
        warnSystemColumn();
        return false;
      }
      const filteredMappings = mappings.filter(
        m => m.targetColumnId !== columnId
      );
      if (
        !mappings.some(m => m.targetColumnId === columnId) &&
        filteredMappings.length >= AUTOMATION_MAPPING_LIMIT
      ) {
        warnMappingLimit();
        return false;
      }
      if (tableId) {
        filteredMappings.push({
          tableId,
          targetColumnId: columnId,
          responseField: '',
          fixedValue: optionId,
        });
      }
      commitMappings(filteredMappings);
      return true;
    },
    [
      commitMappings,
      isProtectedColumn,
      mappings,
      tableId,
      targetColumns,
      warnMappingLimit,
      warnSystemColumn,
    ]
  );

  // sl-mau5: Handle relation lookup toggle — creates/removes MatchConfig
  const handleRelationLookupToggle = useCallback(
    (columnId: string, enabled: boolean) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping) return;

      if (enabled) {
        // Create MatchConfig for this relation lookup
        const column = targetColumns.find(c => c.id === columnId);
        const targetDbId = column?.targetDatabaseId;
        if (!targetDbId) return;

        const mcId = createMatchConfigId();
        const newMc: MatchConfigProps = {
          id: mcId,
          targetTableId: targetDbId,
          multiMatchBehavior: 'ask',
          keys: [
            {
              columnId: 'title',
              sourceField: mapping.responseField,
              matchMode: 'exact',
            },
          ],
        };
        onMatchConfigsChange([...matchConfigs, newMc]);
        // Set matchConfigId on the mapping
        const updatedMappings = mappings.map(m =>
          m.targetColumnId === columnId ? { ...m, matchConfigId: mcId } : m
        );
        onMappingsChange(updatedMappings);
      } else {
        // Remove MatchConfig and clear reference
        const mcId = mapping.matchConfigId;
        if (mcId) {
          onMatchConfigsChange(matchConfigs.filter(mc => mc.id !== mcId));
        }
        const updatedMappings = mappings.map(m =>
          m.targetColumnId === columnId ? { ...m, matchConfigId: undefined } : m
        );
        onMappingsChange(updatedMappings);
      }
    },
    [
      mappings,
      matchConfigs,
      targetColumns,
      onMappingsChange,
      onMatchConfigsChange,
    ]
  );

  // sl-mau5: Handle relation match mode change — updates MatchConfig key
  const handleMatchModeChange = useCallback(
    (columnId: string, mode: RelationMatchMode) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId) return;
      const updatedConfigs = matchConfigs.map(mc => {
        if (mc.id === mapping.matchConfigId && mc.keys.length > 0) {
          const updatedKeys = [...mc.keys];
          updatedKeys[0] = { ...updatedKeys[0], matchMode: mode };
          return { ...mc, keys: updatedKeys };
        }
        return mc;
      });
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  const handleMultiMatchBehaviorChange = useCallback(
    (columnId: string, behavior: MultiMatchBehavior) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId) return;
      onMatchConfigsChange(
        matchConfigs.map(mc =>
          mc.id === mapping.matchConfigId
            ? { ...mc, multiMatchBehavior: behavior }
            : mc
        )
      );
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  // sl-tah4.4: Handle steal behavior change — updates MatchConfig for relation lookup
  const handleStealBehaviorChange = useCallback(
    (columnId: string, behavior: StealBehavior) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId) return;
      const updatedConfigs = matchConfigs.map(mc =>
        mc.id === mapping.matchConfigId
          ? { ...mc, stealBehavior: behavior }
          : mc
      );
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  const outputFields = groupedFields.workflowResponse;
  const rows = useMemo(
    () => buildOutputMappingRows(outputFields, mappings),
    [outputFields, mappings]
  );
  const columnById = useMemo(
    () => new Map(targetColumns.map(column => [column.id, column])),
    [targetColumns]
  );

  /** What a stored mapping writes, in words. */
  const valueLabelOf = useCallback(
    (mapping: OutputMapping): string => {
      if (!mapping.responseField) {
        const column = columnById.get(mapping.targetColumnId);
        return column
          ? resolveOptionLabel(column, mapping.fixedValue ?? '')
          : (mapping.fixedValue ?? '');
      }
      return (
        getResponseFieldInfo(mapping.responseField)?.displayName ||
        mapping.responseFieldName ||
        getDisplayName(mapping.responseField)
      );
    },
    [columnById, getDisplayName, getResponseFieldInfo]
  );

  /**
   * The columns a row can write to. A column takes one value: one that
   * another row writes says so, and picking it moves it to this row.
   */
  const columnGroupsFor = useCallback(
    (ownColumnId: string | undefined): FieldOptionGroup[] => [
      {
        id: 'columns',
        label: t['com.streamlain.automation.sidebar.mapping.side-table'](),
        options: targetColumns.map(column => {
          const protectedColumn = isProtectedColumn(column);
          const taken =
            column.id === ownColumnId
              ? undefined
              : mappings.find(m => m.targetColumnId === column.id);
          return {
            id: column.id,
            label: column.name,
            type: column.type,
            disabled: protectedColumn,
            description: protectedColumn
              ? t['com.streamlain.automation.sidebar.files.system-column']()
              : taken
                ? t['com.streamlain.automation.sidebar.fields.column-taken']({
                    value: valueLabelOf(taken),
                  })
                : undefined,
          };
        }),
      },
    ],
    [isProtectedColumn, mappings, t, targetColumns, valueLabelOf]
  );

  /**
   * The values a row can write that the workflow does not report as a
   * field of its own: the fixed options of a select column, then all fields
   * of the run. A stored field the run no longer has is listed, not offered.
   */
  const valueGroupsFor = useCallback(
    (
      column: ColumnInfo | undefined,
      orphaned?: OutputMapping
    ): FieldOptionGroup[] => {
      const groups: FieldOptionGroup[] = [];
      if (orphaned) {
        groups.push({
          id: 'orphaned',
          label: t['com.streamlain.streamlain.mapping.orphaned-field'](),
          options: [
            {
              id: orphaned.responseField,
              label: valueLabelOf(orphaned),
              type: orphaned.responseFieldType ?? 'text',
              disabled: true,
            },
          ],
        });
      }
      const selectOptions =
        column?.type === 'select'
          ? ((column.data?.options as
              | Array<{ id: string; value: string }>
              | undefined) ?? [])
          : [];
      if (selectOptions.length > 0) {
        groups.push({
          id: 'fixed-values',
          label: t['com.streamlain.streamlain.output.group-fixed'](),
          options: selectOptions.map(option => ({
            id: `${FIXED_PREFIX}${option.id}`,
            label: option.value,
            type: 'select',
          })),
        });
      }
      return [...groups, ...responseFieldGroups];
    },
    [responseFieldGroups, t, valueLabelOf]
  );

  /** A row's value gets a column, or none. Says whether that was stored. */
  const handleColumnChange = useCallback(
    (
      fromColumnId: string | undefined,
      toColumnId: string,
      source: OutputMappingSource
    ): boolean => {
      const column = columnById.get(toColumnId);
      if (column && isProtectedColumn(column)) {
        warnSystemColumn();
        return false;
      }
      const replaces = mappings.some(
        m =>
          m.targetColumnId === fromColumnId || m.targetColumnId === toColumnId
      );
      if (
        toColumnId &&
        !replaces &&
        mappings.length >= AUTOMATION_MAPPING_LIMIT
      ) {
        warnMappingLimit();
        return false;
      }
      commitMappings(
        assignOutputColumn({
          prev: mappings,
          tableId,
          fromColumnId,
          toColumnId,
          source,
        })
      );
      return true;
    },
    [
      columnById,
      commitMappings,
      isProtectedColumn,
      mappings,
      tableId,
      warnMappingLimit,
      warnSystemColumn,
    ]
  );

  /** A column gets the value picked in a row: a fixed option or a field. */
  const handleValueChange = useCallback(
    (columnId: string, optionId: string): boolean =>
      optionId.startsWith(FIXED_PREFIX)
        ? handleFixedValueSelect(columnId, optionId.slice(FIXED_PREFIX.length))
        : handleMappingChange(columnId, optionId),
    [handleFixedValueSelect, handleMappingChange]
  );

  const sourceOfField = useCallback(
    (path: string): OutputMappingSource => {
      const info = getResponseFieldInfo(path);
      return {
        responseField: path,
        responseFieldName: info?.displayName || getDisplayName(path),
        responseFieldType: info?.type,
      };
    },
    [getDisplayName, getResponseFieldInfo]
  );

  const hasFieldSuggestions = responseFields.length > 0;
  const canAutoMatch = hasFieldSuggestions && targetColumns.length > 0;

  if (!tableId) {
    return (
      <SectionHint>
        {t['com.streamlain.streamlain.output.select-target-first']()}
      </SectionHint>
    );
  }
  if (targetColumns.length === 0) {
    return (
      <SectionHint>
        {t['com.streamlain.automation.sidebar.fields.no-columns']()}
      </SectionHint>
    );
  }
  if (!hasWorkflow) {
    return (
      <SectionHint>
        {t['com.streamlain.automation.sidebar.fields.needs-workflow']()}
      </SectionHint>
    );
  }
  if (schemaLoading) return <SectionSkeleton />;

  /** What belongs to a relation column's mapping: its table and the lookup. */
  const relationSettings = (
    column: ColumnInfo | undefined,
    mapping: OutputMapping | undefined
  ): ReactNode => {
    if (!column || !isRelationColumn(column.type) || !mapping?.responseField)
      return null;
    const relationTarget = getRelationTargetInfo(column);
    const relationMc = getRelationMatchConfig(column.id);
    return (
      <div className={styles.relation}>
        {relationTarget && (
          <div className={styles.relationTarget}>
            <span>
              {t['com.streamlain.automation.sidebar.relation.linked-table']()}
            </span>
            <button
              type="button"
              className={styles.relationLink}
              onClick={() => handleOpenRelationTarget(column)}
              title={t['com.streamlain.automation.sidebar.relation.open-table'](
                { table: relationTarget.tableName }
              )}
            >
              {relationTarget.tableName}
            </button>
          </div>
        )}
        <SectionControlRow
          label={t['com.streamlain.streamlain.mapping.smart-lookup']()}
          description={t[
            'com.streamlain.automation.sidebar.relation.lookup-hint'
          ]()}
          coachmarkTarget="relation-lookup-toggle"
        >
          <Switch
            checked={!!mapping.matchConfigId}
            onChange={checked => handleRelationLookupToggle(column.id, checked)}
          />
        </SectionControlRow>
        {mapping.matchConfigId && relationMc && (
          <MatchConfigEditor
            matchMode={relationMc.keys[0]?.matchMode ?? 'exact'}
            matchModeOptions={MATCH_MODE_WITH_AUTO_OPTIONS}
            onMatchModeChange={mode =>
              handleMatchModeChange(column.id, mode as RelationMatchMode)
            }
            columns={relationTarget?.columns}
            selectedColumnId={
              relationMc.keys[0]?.columnId === 'title'
                ? undefined
                : relationMc.keys[0]?.columnId
            }
            onColumnChange={columnId =>
              handleLookupMatchColumnChange(column.id, columnId)
            }
            multiMatchBehavior={relationMc.multiMatchBehavior ?? 'ask'}
            onMultiMatchBehaviorChange={behavior =>
              handleMultiMatchBehaviorChange(column.id, behavior)
            }
            stealBehavior={
              column.inverseCardinality === 'one'
                ? (relationMc.stealBehavior ?? 'fail')
                : undefined
            }
            onStealBehaviorChange={behavior =>
              handleStealBehaviorChange(column.id, behavior)
            }
          />
        )}
      </div>
    );
  };

  const fitOf = (sourceType: string | undefined, column?: ColumnInfo) =>
    sourceType && column ? (
      <CompatDot compatibility={columnCompatibility(sourceType, column.type)} />
    ) : undefined;
  const pickColumn =
    t['com.streamlain.automation.sidebar.mapping.pick-column']();
  const pickValue = t['com.streamlain.automation.sidebar.fields.pick-value']();
  const searchFields = t['com.streamlain.streamlain.mapping.search-fields']();
  /** Auto-match fills what has no column yet: a value written once stays. */
  const unmappedResponseFields = responseFields.filter(
    field => !mappings.some(mapping => mapping.responseField === field.path)
  );

  return (
    <SectionStack>
      {isAutoMatchOpen && (
        <AutoMatchDialog
          open={isAutoMatchOpen}
          onClose={() => setIsAutoMatchOpen(false)}
          sourceFields={unmappedResponseFields}
          targetFields={targetColumns
            .filter(column => !isProtectedColumn(column))
            .map(c => ({
              id: c.id,
              name: c.name,
              type: c.type,
            }))}
          existingTargetIds={new Set(mappings.map(m => m.targetColumnId))}
          sourceLabel={t[
            'com.streamlain.automation.sidebar.mapping.side-workflow'
          ]()}
          targetLabel={t[
            'com.streamlain.automation.sidebar.mapping.side-table'
          ]()}
          workflowSide="source"
          choiceSide="target"
          onApply={suggestions => {
            const selectedIds = new Set(suggestions.map(s => s.targetColumnId));
            const preserved = mappings.filter(
              m => !selectedIds.has(m.targetColumnId)
            );
            const availableSlots = Math.max(
              0,
              AUTOMATION_MAPPING_LIMIT - preserved.length
            );
            const limitedSuggestions = suggestions.slice(0, availableSlots);
            if (limitedSuggestions.length < suggestions.length) {
              warnMappingLimit();
            }
            commitMappings([
              ...preserved,
              ...limitedSuggestions.map(s => ({
                tableId,
                targetColumnId: s.targetColumnId,
                responseField: s.responseField,
                responseFieldName: s.responseFieldName,
                responseFieldType: responseFields.find(
                  f => f.path === s.responseField
                )?.type,
              })),
            ]);
          }}
        />
      )}

      {filesTable && (
        <SectionHint>
          {t['com.streamlain.automation.sidebar.files.hint']()}
        </SectionHint>
      )}
      {canAutoMatch && (
        <Button
          block
          prefix={<AiIcon />}
          onClick={() => setIsAutoMatchOpen(true)}
          data-testid="output-auto-match"
          data-attention-action
        >
          {t['com.streamlain.streamlain.automap.button']()}
        </Button>
      )}
      {!hasFieldSuggestions && (
        <>
          <SectionHint>
            {t['com.streamlain.automation.sidebar.fields.no-response-fields']()}
          </SectionHint>
          {onOpenWorkflow && (
            <Button block onClick={onOpenWorkflow}>
              {t['com.streamlain.automation.sidebar.open-workflow']()}
            </Button>
          )}
        </>
      )}

      {(rows.length > 0 || draft) && (
        <SectionMappingGrid
          sourceLabel={t[
            'com.streamlain.automation.sidebar.mapping.side-workflow'
          ]()}
          targetLabel={t[
            'com.streamlain.automation.sidebar.mapping.side-table'
          ]()}
          workflowSide="source"
        >
          {rows.map(row => {
            const { mapping, field } = row;
            const column = mapping
              ? columnById.get(mapping.targetColumnId)
              : undefined;

            // Without fields of the run nothing can be chosen: what is saved.
            if (!hasFieldSuggestions && mapping) {
              return (
                <SectionMappingPair
                  key={row.key}
                  fixed="both"
                  source={
                    <MappingCell
                      name={valueLabelOf(mapping)}
                      type={
                        mapping.responseField
                          ? mapping.responseFieldType
                          : undefined
                      }
                      note={t['com.streamlain.streamlain.output.group-fixed']()}
                    />
                  }
                  target={
                    <MappingCell
                      name={column?.name ?? mapping.targetColumnId}
                      type={column?.type}
                    />
                  }
                />
              );
            }

            // A field the workflow reports: given; its column is chosen.
            if (field) {
              return (
                <SectionMappingPair
                  key={row.key}
                  fixed="source"
                  link={fitOf(field.type, column)}
                  source={
                    <MappingCell
                      name={field.displayName || getDisplayName(field.path)}
                      type={field.type}
                      required={field.required}
                    />
                  }
                  target={
                    <TypedFieldSelect
                      stacked
                      value={mapping?.targetColumnId ?? ''}
                      groups={columnGroupsFor(mapping?.targetColumnId)}
                      onChange={columnId =>
                        handleColumnChange(
                          mapping?.targetColumnId,
                          columnId,
                          sourceOfField(field.path)
                        )
                      }
                      placeholder={pickColumn}
                      ariaLabel={field.displayName || field.path}
                      targetType={field.type}
                      getCompatibility={columnCompatibility}
                      searchable
                      searchPlaceholder={searchFields}
                    />
                  }
                >
                  {relationSettings(column, mapping)}
                </SectionMappingPair>
              );
            }

            // Another value: both what is written and where are chosen.
            if (!mapping) return null;
            const fieldInfo = mapping.responseField
              ? getResponseFieldInfo(mapping.responseField)
              : undefined;
            const orphaned = !!mapping.responseField && !fieldInfo;
            return (
              <SectionMappingPair
                key={row.key}
                fixed="none"
                link={
                  orphaned ? (
                    <CompatDot
                      compatibility="incompatible"
                      tooltip={t[
                        'com.streamlain.streamlain.mapping.orphaned-field'
                      ]()}
                    />
                  ) : (
                    fitOf(fieldInfo?.type, column)
                  )
                }
                source={
                  <TypedFieldSelect
                    stacked
                    value={
                      mapping.responseField ||
                      `${FIXED_PREFIX}${mapping.fixedValue ?? ''}`
                    }
                    groups={valueGroupsFor(
                      column,
                      orphaned ? mapping : undefined
                    )}
                    onChange={optionId =>
                      handleValueChange(mapping.targetColumnId, optionId)
                    }
                    placeholder={pickValue}
                    ariaLabel={pickValue}
                    targetType={column?.type}
                    searchable
                    searchPlaceholder={searchFields}
                  />
                }
                target={
                  <TypedFieldSelect
                    stacked
                    value={mapping.targetColumnId}
                    groups={columnGroupsFor(mapping.targetColumnId)}
                    onChange={columnId => {
                      if (mapping.responseField) {
                        handleColumnChange(mapping.targetColumnId, columnId, {
                          responseField: mapping.responseField,
                          responseFieldName: mapping.responseFieldName,
                          responseFieldType: mapping.responseFieldType,
                        });
                        return;
                      }
                      // A fixed option belongs to its column: another column
                      // starts over with that column chosen.
                      if (columnId === mapping.targetColumnId) return;
                      commitMappings(
                        mappings.filter(
                          m => m.targetColumnId !== mapping.targetColumnId
                        )
                      );
                      if (columnId) setDraft({ source: '', columnId });
                    }}
                    placeholder={pickColumn}
                    ariaLabel={pickColumn}
                    targetType={fieldInfo?.type}
                    getCompatibility={
                      fieldInfo ? columnCompatibility : undefined
                    }
                    searchable
                    searchPlaceholder={searchFields}
                  />
                }
              >
                {relationSettings(column, mapping)}
              </SectionMappingPair>
            );
          })}

          {draft && (
            <SectionMappingPair
              fixed="none"
              source={
                <TypedFieldSelect
                  stacked
                  value={draft.source}
                  groups={valueGroupsFor(columnById.get(draft.columnId))}
                  onChange={optionId => {
                    if (!draft.columnId || !optionId) {
                      setDraft({ ...draft, source: optionId });
                    } else if (handleValueChange(draft.columnId, optionId)) {
                      setDraft(null);
                    }
                  }}
                  placeholder={pickValue}
                  ariaLabel={pickValue}
                  targetType={columnById.get(draft.columnId)?.type}
                  searchable
                  searchPlaceholder={searchFields}
                />
              }
              target={
                <TypedFieldSelect
                  stacked
                  value={draft.columnId}
                  groups={columnGroupsFor(undefined)}
                  onChange={columnId => {
                    // A fixed option belongs to the column it was picked for.
                    const source = draft.source.startsWith(FIXED_PREFIX)
                      ? ''
                      : draft.source;
                    if (!columnId || !source) {
                      setDraft({ source, columnId });
                    } else if (
                      handleColumnChange(
                        undefined,
                        columnId,
                        sourceOfField(source)
                      )
                    ) {
                      setDraft(null);
                    }
                  }}
                  placeholder={pickColumn}
                  ariaLabel={pickColumn}
                  targetType={getResponseFieldInfo(draft.source)?.type}
                  getCompatibility={
                    getResponseFieldInfo(draft.source)
                      ? columnCompatibility
                      : undefined
                  }
                  searchable
                  searchPlaceholder={searchFields}
                />
              }
            />
          )}
        </SectionMappingGrid>
      )}

      {hasFieldSuggestions && (
        <SectionAddLink
          onClick={() => setDraft(draft ? null : EMPTY_DRAFT)}
          data-testid="output-add-mapping"
        >
          {draft
            ? t['com.streamlain.streamlain.common.cancel']()
            : t['com.streamlain.automation.sidebar.fields.add-mapping']()}
        </SectionAddLink>
      )}
    </SectionStack>
  );
};
